// Сторож признака оседания источника (`editor.draftSourceSettling`). Владелец таймера
// ОДИН — `draftProbeMiddleware`, который видит все экшены: признак ставят и зонд, и
// `ModerationPoller` на смене источника, а лок без сторожа мог бы стать вечным.
import type { UnknownAction } from '@reduxjs/toolkit'
import { draftSourceSettled, setDraftSourceSettling } from '@/features/editor/editorSlice'
import { coursesApi } from '@/features/editor/coursesApi'
import { DRAFT_SETTLE_MAX_MS } from './draftSourceSettling'
import { expectedReadIdOf } from './expectedReadId'
import type { ModerationSelectState } from './refetchCourseModeration'

type EditorCourseSelectState = Parameters<
  ReturnType<typeof coursesApi.endpoints.getEditorCourse.select>
>[0]

export type WatchState = ModerationSelectState &
  EditorCourseSelectState & { editor: { draftSourceSettling: number | null } }

/**
 * Каждая установка признака с не-`null` (кем бы она ни пришла) вооружает таймер заново;
 * снятие признака — разоружает. Срабатывание снимает признак того курса, что его поставил
 * (`onExpire` — до снятия: владелец отпускает держание признака летящим зондом).
 */
export const createSettlingWatchdog = (
  dispatch: (action: UnknownAction) => unknown,
  onExpire: () => void,
) => {
  let timer: ReturnType<typeof setTimeout> | undefined
  return (action: unknown, state: WatchState): void => {
    if (setDraftSourceSettling.match(action) && action.payload !== null) {
      const courseId = action.payload
      clearTimeout(timer)
      timer = setTimeout(() => {
        onExpire()
        dispatch(draftSourceSettled(courseId))
      }, DRAFT_SETTLE_MAX_MS)
      return
    }
    if (timer !== undefined && state.editor.draftSourceSettling === null) {
      clearTimeout(timer)
      timer = undefined
    }
  }
}

/**
 * Редактор ещё не читает тот источник, который назвал кеш `/moderation`: переезд впереди
 * (или кеша курса нет вовсе). Без подписки — только чтение кешей.
 */
export const isSourceBehind = (state: WatchState, courseId: number): boolean =>
  coursesApi.endpoints.getEditorCourse.select(courseId)(state).data?.readId !==
  expectedReadIdOf(state, courseId)
